import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import Map, { Marker, Popup, Source, Layer, MapRef } from 'react-map-gl/mapbox'
import { UserHistoryByDateResp } from 'domain/payload/response/user/UserViewResp';
import { LineLayer } from 'mapbox-gl';
import { useNavPillsIndicator } from 'hooks/useNavPillsIndicator';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN ?? ''

const normalizeCoordinate = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed)) return null;
  return parsed;
};

interface DailyRouteViewProps {
  historyByDate: UserHistoryByDateResp[] | null;
  loading: boolean;
  contentHeight: string;
  selectedDate: string;
  onDateChange: (date: string) => void;
  formatGapTime?: (seconds: number | null) => string;
}

type ViewMode = 'list' | 'map';

interface MapItem {
  id: string;
  jobName: string;
  jobDescription: string;
  latitude: number;
  longitude: number;
  status: {
    key: number;
    value: string;
  };
  submitDate: string;
  workerName: string;
  workerId: string;
  gapTime: number | null;
}

const DailyRouteView: React.FC<DailyRouteViewProps> = ({
  historyByDate,
  loading,
  contentHeight,
  selectedDate,
  onDateChange,
  formatGapTime
}) => {
  const historyByDateListRef = useRef<HTMLDivElement>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedMapItem, setSelectedMapItem] = useState<MapItem | null>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [hoveredMarker, setHoveredMarker] = useState<MapItem | null>(null);
  const [hoveredLineIndex, setHoveredLineIndex] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [tooltipText, setTooltipText] = useState<string>('');
  const mapRef = useRef<MapRef | null>(null)
  const [mousePosition, setMousePosition] = useState<{ lat: number; lng: number } | null>(null);
  const [isLineHovered, setIsLineHovered] = useState(false);

  const dailyRouteData = (historyByDate || []).filter(
    (item) => (item.submitDate || '').slice(0, 10) === selectedDate
  );

  const hasHistoryByDate = dailyRouteData.length > 0;

  useEffect(() => {
    if (viewMode === 'map') {
      setSelectedMapItem(null);
      setHoveredMarker(null);
      setHoveredLineIndex(null);
      setTooltipPosition(null);
      setMousePosition(null);
      setIsLineHovered(false);
      setIsMapLoaded(false);
    }
  }, [viewMode, selectedDate]);

  const mapItems = useMemo(() => {
    if (!hasHistoryByDate) return [];

    return dailyRouteData
      .map((item) => {
        const latitude = normalizeCoordinate(item.latitude);
        const longitude = normalizeCoordinate(item.longitude);

        if (
          latitude === null ||
          longitude === null ||
          latitude < -90 ||
          latitude > 90 ||
          longitude < -180 ||
          longitude > 180
        ) {
          return null;
        }

        return {
          id: item.id,
          jobName: item.jobName,
          jobDescription: item.jobDescription,
          latitude,
          longitude,
          status: item.status,
          submitDate: item.submitDate,
          workerName: item.workerName,
          workerId: item.workerId,
          gapTime: item.gapTime,
        } as MapItem;
      })
      .filter((item): item is MapItem => item !== null)
      .sort((a, b) => new Date(a.submitDate).getTime() - new Date(b.submitDate).getTime());
  }, [dailyRouteData, hasHistoryByDate]);

  const polylinePath = useMemo(() => {
    return mapItems.map(item => ({
      lat: item.latitude,
      lng: item.longitude
    }));
  }, [mapItems]);

  const routeLineLayer: LineLayer = {
    id: 'route-line',
    type: 'line',
    source: 'route-source',
    layout: { 'line-join': 'round', 'line-cap': 'round' },
    paint: {
      'line-color': '#0d6efd',
      'line-width': [
        'case',
        ['==', ['get', 'index'], hoveredLineIndex ?? -1], 5,
        3
      ],
      'line-opacity': [
        'case',
        ['==', ['get', 'index'], hoveredLineIndex ?? -1], 0.95,
        0.65
      ],
    },
  }

  const lineSegments = useMemo(() => {
    const segments = [];
    for (let i = 0; i < mapItems.length - 1; i++) {
      const endItem = mapItems[i + 1];
      const startPoint = polylinePath[i];
      const endPoint = polylinePath[i + 1];

      segments.push({
        index: i,
        startPoint,
        endPoint,
        gapTime: endItem.gapTime,
        midpoint: {
          lat: (startPoint.lat + endPoint.lat) / 2,
          lng: (startPoint.lng + endPoint.lng) / 2
        }
      });
    }
    return segments;
  }, [mapItems, polylinePath]);

  const defaultFormatGapTime = (seconds: number | null) => {
    if (seconds === null || seconds === undefined) return '-';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatGapTimeFn = formatGapTime || defaultFormatGapTime;

  const formatDisplayDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('id-ID', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatDisplayTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('id-ID', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return '';
    }
  };

  const getTimeColor = (time: string) => {
    if (!time) return '#000';
    const hour = parseInt(time.split(':')[0], 10);

    if (hour >= 6 && hour <= 8) return '#08BFA7';
    if (hour >= 9 && hour <= 11) return '#08BF2D';
    if (hour >= 12 && hour <= 14) return '#BFA408';
    if (hour >= 15 && hour <= 18) return '#BF7908';
    return '#BF0808';
  };

  const handleViewModeChange = (mode: ViewMode) => {
    setSelectedMapItem(null);
    setHoveredMarker(null);
    setHoveredLineIndex(null);
    setTooltipPosition(null);
    setMousePosition(null);
    setIsLineHovered(false);
    setIsMapLoaded(false);
    setViewMode(mode);
  };

  const { navRef: viewModeNavRef, indicatorStyle: viewModeIndicatorStyle } = useNavPillsIndicator(handleViewModeChange);

  const renderListView = () => {
    if (loading) {
      return (
        <div className="text-center py-4">
          <div className="spinner-border spinner-border-sm text-primary" role="status">
            <span className="visually-hidden">Loading daily route...</span>
          </div>
          <p className="mt-2">Loading daily route...</p>
        </div>
      );
    }

    if (!hasHistoryByDate) {
      return (
        <div className="text-center d-flex flex-column justify-content-center align-items-center" style={{ height: '100%' }}>
          <i className="fa-solid fa-route text-muted fa-3x mb-2"></i>
          <p className="text-muted">No daily route data for {formatDisplayDate(selectedDate)}</p>
          <small className="text-muted mb-3">Select a different date to see the route</small>
        </div>
      );
    }

    return (
      <div className="p-3">
        {dailyRouteData.map((task, index) => {
          const formattedDate = formatDisplayDate(task.submitDate);
          const formattedTime = formatDisplayTime(task.submitDate);

          return (
            <div key={task.id} className="mb-4">
              <div
                className="d-flex align-items-center p-4 shadow-sm rounded-4 border"
                style={{
                  borderColor: '#4a90e2',
                  borderWidth: '2px',
                  backgroundColor: '#fff',
                  gap: '16px',
                }}
              >
                <div style={{ minWidth: '70px', textAlign: 'center' }}>
                  <h5
                    className="fw-bold m-0"
                    style={{ fontSize: '22px', color: `${getTimeColor(formattedTime)}` }}
                  >
                    {formattedTime}
                  </h5>
                </div>

                <div
                  style={{
                    width: '1px',
                    alignSelf: 'stretch',
                    backgroundColor: '#ccc',
                  }}
                ></div>

                <div className="flex-grow-1 d-flex flex-column">
                  <div className="d-flex justify-content-start mb-1">
                    <small className="text-muted">
                      <strong>Result ID :</strong> {task.id}
                    </small>
                  </div>
                  <h5 className="fw-bold mb-1" style={{ fontSize: '18px', color: '#000' }}>
                    {task.jobName || '-'}
                  </h5>
                  <span className="text-dark fw-semibold d-flex align-items-center">
                    {task.jobDescription || 'No description'}
                  </span>

                  <div className="d-flex justify-content-between align-items-center my-2">
                    <span className="text-muted d-flex align-items-center ">
                      <i className="fa-solid fa-location-dot me-2"></i>
                      {task.latitude?.toFixed(6)}, {task.longitude?.toFixed(6)}
                    </span>
                    <span
                      className="px-3 py-1 fw-semibold text-white"
                      style={{
                        backgroundColor:
                          task.status.key === 4
                            ? '#28a745'
                            : task.status.key === 5
                              ? '#dc3545'
                              : '#ffc107',
                        borderRadius: '9999px',
                        fontSize: '14px',
                      }}
                    >
                      {task.status.value || 'Unknown'}
                    </span>
                  </div>
                </div>
              </div>

              {index < (dailyRouteData.length || 0) - 1 && (
                <div className="text-center my-2 mt-4">
                  <div className='d-flex justify-content-center align-items-center gap-2'>
                    <div
                      style={{
                        height: '1.5px',
                        width: '100%',
                        background:
                          'linear-gradient(to right, transparent 0%, #ccc 40%, #ccc 60%)',
                        marginTop: '4px',
                        position: 'relative',
                      }}
                    >
                      <i
                        className="fa-solid text-secondary"
                        style={{
                          position: 'absolute',
                          left: '50%',
                          transform: 'translateX(-50%) translateY(-6px)',
                          background: '#fff',
                          fontSize: '10px',
                        }}
                      ></i>
                    </div>
                    <div
                      className="fw-semibold text-secondary"
                      style={{
                        fontSize: '16px',
                        color: '#6c757d',
                      }}
                    >
                      {dailyRouteData[index + 1]?.gapTime !== null && dailyRouteData[index + 1]?.gapTime !== undefined
                        ? formatGapTimeFn(dailyRouteData[index + 1].gapTime)
                        : 'First Task'}
                    </div>
                    <div
                      style={{
                        height: '1.5px',
                        width: '100%',
                        background:
                          'linear-gradient(to left, transparent 0%, #ccc 40%, #ccc 60%)',
                        marginTop: '4px',
                        position: 'relative',
                      }}
                    >
                      <i
                        className="fa-solid text-secondary"
                        style={{
                          position: 'absolute',
                          left: '50%',
                          transform: 'translateX(-50%) translateY(-6px)',
                          background: '#fff',
                          fontSize: '10px',
                        }}
                      ></i>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderMapContent = () => {
    if (loading) {
      return (
        <div className="text-center py-5" style={{ height: contentHeight }}>
          <div className="spinner-border spinner-border-sm text-primary" role="status">
            <span className="visually-hidden">Loading map...</span>
          </div>
          <p className="mt-2">Loading map...</p>
        </div>
      );
    }

    if (!hasHistoryByDate) {
      return (
        <div className="text-center py-5" style={{ height: contentHeight }}>
          <i className="fa-solid fa-map text-muted fa-3x mb-2"></i>
          <p className="text-muted">No daily route data for {formatDisplayDate(selectedDate)}</p>
          <small className="text-muted">Select a different date to see the route on map</small>
        </div>
      );
    }

    if (mapItems.length === 0) {
      return (
        <div className="text-center py-5" style={{ height: contentHeight }}>
          <i className="fa-solid fa-map-marker-slash text-muted fa-3x mb-2"></i>
          <p className="text-muted">No tasks with location data available</p>
        </div>
      );
    }

    const routeGeoJSON: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: lineSegments.map((segment, index) => ({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [
            [segment.startPoint.lng, segment.startPoint.lat],
            [segment.endPoint.lng, segment.endPoint.lat],
          ],
        },
        properties: { index },
      })),
    }

    const handleMapLoad = () => {
      if (!mapRef.current || mapItems.length === 0) return
      const lngs = mapItems.map(i => i.longitude)
      const lats = mapItems.map(i => i.latitude)
      mapRef.current.fitBounds(
        [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
        { padding: 60, duration: 500 }
      )
    }

    return (
      <div className="position-relative h-100" style={{ height: contentHeight }}>
        <Map
          ref={mapRef}
          mapboxAccessToken={MAPBOX_TOKEN}
          initialViewState={{
            longitude: mapItems[0].longitude,
            latitude: mapItems[0].latitude,
            zoom: 12,
          }}
          style={{ width: '100%', height: '100%' }}
          mapStyle="mapbox://styles/mapbox/streets-v12"
          onLoad={handleMapLoad}
          onMouseMove={(e) => {
            const features = e.target.queryRenderedFeatures(e.point, {
              layers: ['route-line']
            })
            if (features.length > 0) {
              const idx = features[0].properties?.index
              setHoveredLineIndex(idx ?? null)
              e.target.getCanvas().style.cursor = 'pointer'
            } else {
              setHoveredLineIndex(null)
              e.target.getCanvas().style.cursor = ''
            }
          }}
          onMouseLeave={() => {
            setHoveredLineIndex(null)
          }}
        >
          {mapItems.length > 1 && (
            <Source id="route-source" type="geojson" data={routeGeoJSON}>
              <Layer {...routeLineLayer} />
            </Source>
          )}

          {mapItems.map((item, index) => {
            const isFirst = index === 0
            const isLast = index === mapItems.length - 1

            return (
              <Marker
                key={`${item.id}-${index}`}
                longitude={item.longitude}
                latitude={item.latitude}
                anchor="bottom"
                onClick={(e) => {
                  e.originalEvent.stopPropagation()
                  setSelectedMapItem(item)

                  mapRef.current?.flyTo({
                    center: [item.longitude, item.latitude],
                    duration: 500,
                    offset: [0, 100],
                  })
                }}
              >
                {isFirst ? (
                  <img
                    src="/assets/markers/icon-marker-selected.svg"
                    style={{ width: 32, height: 32, cursor: 'pointer' }}
                    title={item.jobName}
                    alt="start"
                  />
                ) : isLast ? (
                  <img
                    src="/assets/markers/icon-marker.svg"
                    style={{ width: 24, height: 24, cursor: 'pointer' }}
                    title={item.jobName}
                    alt="end"
                  />

                ) : (
                  <img
                    src="/assets/markers/icon-marker.svg"
                    style={{ width: 24, height: 24, cursor: 'pointer' }}
                    title={item.jobName}
                    alt=""
                  />
                )}
              </Marker>
            )
          })}

          {lineSegments.map((segment, index) => (
            segment.gapTime !== null && hoveredLineIndex === index && (
              <Marker
                key={`gap-${index}`}
                longitude={segment.midpoint.lng}
                latitude={segment.midpoint.lat}
                anchor="center"
              >
                <div style={{
                  background: 'white',
                  border: '1px solid #3f51b5',
                  borderRadius: 4,
                  padding: '2px 8px',
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#1a237e',
                  pointerEvents: 'none',
                  boxShadow: '0 2px 6px rgba(63,81,181,0.3)',
                  whiteSpace: 'nowrap',
                }}>
                  <i className="fa-regular fa-clock me-1" style={{ fontSize: 10 }} />
                  {formatGapTimeFn(segment.gapTime)}
                </div>
              </Marker>
            )
          ))}

          {selectedMapItem && (
            <Popup
              longitude={selectedMapItem.longitude}
              latitude={selectedMapItem.latitude}
              anchor="bottom"
              offset={[0, -30]}
              onClose={() => setSelectedMapItem(null)}
              closeOnClick={false}
              maxWidth="220px"
            >
              <div style={{ maxWidth: 200 }}>
                <h6 className="fw-bold mb-1">{selectedMapItem.jobName}</h6>
                <div className="mb-1">
                  <small className="badge bg-light text-dark border">Daily Task</small>
                </div>
                <p className="mb-1 small">{selectedMapItem.jobDescription || 'No description'}</p>
                <div className="mb-1 small">
                  <span className={`badge ${selectedMapItem.status.key === 4 ? 'bg-success' :
                    selectedMapItem.status.key === 5 ? 'bg-danger' : 'bg-warning'
                    }`}>
                    {selectedMapItem.status.value || 'Unknown'}
                  </span>
                </div>
                <div className="small text-muted">
                  <i className="fa-solid fa-location-dot me-1"></i>
                  {selectedMapItem.latitude.toFixed(6)}, {selectedMapItem.longitude.toFixed(6)}
                </div>
                <div className="small text-muted mt-1">
                  <i className="fa-regular fa-calendar me-1"></i>
                  {formatDisplayDate(selectedMapItem.submitDate)}, {formatDisplayTime(selectedMapItem.submitDate)}
                </div>
              </div>
            </Popup>
          )}
        </Map>

        <div className="position-absolute top-0 m-2" style={{ zIndex: 1 }}>
          <div className="bg-white rounded shadow-sm p-2">
            <div className="d-flex align-items-center mb-1">
              <img
                src="/assets/markers/icon-marker-selected.svg"
                style={{ width: 16, height: 16, marginRight: 6 }}
                alt=""
              />
              <small>Start</small>
            </div>
            <div className="d-flex align-items-center mb-1">
              <img
                src="/assets/markers/icon-marker.svg"
                style={{ width: 16, height: 16, marginRight: 6 }}
                alt=""
              />
              <small>End</small>
            </div>
            <div className="d-flex align-items-center">
              <div style={{ width: 10, height: 2, background: '#0d6efd', marginRight: 6 }} />
              <small>{mapItems.length} locations</small>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMapView = () => {
    return renderMapContent();
  };

  return (
    <>
      <div className="p-3 border-bottom bg-light">
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <label className="me-2 fw-semibold">Select Date:</label>
            <input
              type="date"
              className="form-control form-control-sm w-auto"
              value={selectedDate}
              onChange={(e) => {
                onDateChange(e.target.value);
              }}
              max={new Date().toISOString().split('T')[0]}
            />
          </div>

          <div ref={viewModeNavRef} className="nav-pills-animated h-100">
            <div
              className="nav-pill-indicator"
              style={viewModeIndicatorStyle}
            />
            <button
              type="button"
              className={`nav-link btn ${viewMode === "list" ? "active" : ""}`}
              onClick={() => handleViewModeChange('list')}
            >
              <i className="fa-solid fa-list me-1"></i> List
            </button>
            <button
              type="button"
              className={`nav-link btn ${viewMode === "map" ? "active" : ""}`}
              onClick={() => handleViewModeChange('map')}
            >
              <i className="fa-solid fa-map me-1"></i> Map
            </button>
          </div>
        </div>
      </div>

      <div
        ref={historyByDateListRef}
        style={{
          height: contentHeight,
          minHeight: '400px',
          position: 'relative',
        }}
      >
        {viewMode === 'list' && (
          <div style={{ overflowY: 'auto', height: '100%' }}>
            {renderListView()}
          </div>
        )}

        {viewMode === 'map' && (
          <div style={{ height: '100%', position: 'absolute', inset: 0 }}>
            {renderMapView()}
          </div>
        )}
      </div>
    </>
  );
};

export default DailyRouteView;
